import { defineStore } from 'pinia'
import logo from '@/assets/logo_white.svg'
import companyService from '@/services/company.service'
import sessionStorageService from '@/core/sessionstorage.service'
import router from '@/router'
import { detectEnvironment } from '@/core/helper-functions'
import { skylineConfig, faviconDimensions } from '@/core/config'
import { useInvestorStore } from '@/stores/investor'
import { useGeneralStore } from '@/stores/general'

/* One definition of the unbranded defaults. These used to be duplicated in three
   places with two different colours, so an investor with no white-label config
   fell back to the pre-rebrand gold instead of this. */
const DEFAULT_BRANDING = {
  logoUrl: logo,
  darkHeader: false,
  color: '#0D2A4F',
  secondaryColor: '#0D2A4F',
  fromWhiteLabeledDomain: false
}

export const useCompanyStore = defineStore('company', {
  state: () => ({
    ...DEFAULT_BRANDING,
    favicon: null
  }),

  getters: {
    whiteLabel: state => {
      if (sessionStorageService.hasKey('company')) return sessionStorageService.getItem('company')

      // If the request comes from a non-white labelled domain (like investor.raisr.com
      // or dashboard.investorportal.com), use the current investor to detect the white label
      // configuration.
      // See: https://exemptedge.atlassian.net/browse/EE-16627.
      if (!state.fromWhiteLabeledDomain) {
        const investorStore = useInvestorStore()

        if (investorStore.currentInvestor) {
          const investorConfig = { ...DEFAULT_BRANDING }
          const currentInvestor = investorStore.currentInvestor

          if (currentInvestor && currentInvestor.company) {
            if (currentInvestor.company.whiteLabel) {
              const whiteLabel = currentInvestor.company.whiteLabel

              if (whiteLabel.color) {
                investorConfig.color = whiteLabel.color
                investorConfig.secondaryColor = whiteLabel.color
              }
              if (whiteLabel.logo_url) investorConfig.logoUrl = whiteLabel.logo_url
              investorConfig.darkHeader = whiteLabel.dark_header

              if (currentInvestor.company.logo) investorConfig.logoUrl = currentInvestor.company.logo
            }
          }

          /* isSkyline lives on the general store, not the investor store. Vuex kept every
             getter in one flat namespace, so the pre-migration code could read it from
             anywhere; Pinia namespaces per store, so it has to be resolved explicitly. */
          if (useGeneralStore().isSkyline) {
            investorConfig.color = skylineConfig.color
            investorConfig.secondaryColor = skylineConfig.secondaryColor
          }

          return { ...state, ...investorConfig }
        }
      }

      return state
    },

    baseRouteConfig: state => {
      if (detectEnvironment() === 'production') {
        return {}
      }

      const config = { query: {} }
      if (state.fromWhiteLabeledDomain) {
        if (router.currentRoute.value.query.whitelabel) {
          config.query.whitelabel = router.currentRoute.value.query.whitelabel
        } else {
          const urlSearchParams = new URLSearchParams(window.location.search)
          const params = Object.fromEntries(urlSearchParams.entries())

          if (Object.hasOwn(params, 'whitelabel')) config.query.whitelabel = params.whitelabel
        }
      }
      return config
    }
  },

  actions: {
    setCompany (value) {
      this.logoUrl = value.logoUrl
      this.color = value.color
      this.secondaryColor = value.secondaryColor
      this.darkHeader = value.darkHeader
      this.favicon = value.favicon

      if (detectEnvironment() === 'production' && this.fromWhiteLabeledDomain) {
        sessionStorageService.setItem('company', value)
      }

      // Use the favicon value if any
      if (this.favicon) {
        document.querySelectorAll('head link[data-dynamic]').forEach(element => element.remove())

        const fragment = new DocumentFragment()

        for (const iconType in faviconDimensions) {
          faviconDimensions[iconType].forEach(dimension => {
            const faviconUrl = process.env.VUE_APP_API + this.favicon + `?dimension=${dimension}`
            const faviconElement = document.createElement('link')
            faviconElement.setAttribute('rel', iconType)
            faviconElement.setAttribute('sizes', `${dimension}x${dimension}`)
            faviconElement.setAttribute('href', faviconUrl)
            faviconElement.setAttribute('data-dynamic', true)

            if (iconType === 'icon') {
              faviconElement.setAttribute('type', 'image/png')
            }

            fragment.append(faviconElement)
          })
        }

        document.head.append(fragment)
      } else {
        const faviconUrl = `${process.env.BASE_URL}favicon.png`
        const faviconElement = document.createElement('link')
        faviconElement.setAttribute('rel', 'icon')
        faviconElement.setAttribute('href', faviconUrl)
        faviconElement.setAttribute('data-dynamic', true)
        document.head.append(faviconElement)
      }
    },

    setFromWhiteLabeledDomain (value) {
      this.fromWhiteLabeledDomain = value
    },

    async initializeWhiteLabel () {
      if (sessionStorageService.hasKey('company')) return

      const baseUrl = Object.hasOwn(process.env, 'VUE_APP_ASSETS_BASE_URL')
        ? process.env.VUE_APP_ASSETS_BASE_URL
        : process.env.VUE_APP_API

      try {
        const { data } = await companyService.getWhiteLabel()

        const logo = baseUrl + data.logo_url

        this.setFromWhiteLabeledDomain(true)

        this.setCompany({
          logoUrl: logo,
          darkHeader: data.dark_header,
          color: data.color,
          secondaryColor: data.color,
          favicon: data.favicon
        })
      } catch (error) {
        this.setCompany(this.$state)
      }
    }
  }
})
